import { useMutation, useQueryClient } from '@tanstack/react-query'
import { strapiPost, strapiDelete } from '@/lib/strapi'
import { db } from '@/lib/db/database'
import type { Area } from '@/lib/types/area'
import type { Table } from '@/lib/types/table'
import type { BookingTable } from '@/lib/types/booking'
import { toast } from 'sonner'

interface CreateBookingTableInput {
  bookingDocumentId: string
  table: Table
  area: Area
}

export function useCreateBookingTable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: async ({ bookingDocumentId, table, area }: CreateBookingTableInput) => {
      const resp = await strapiPost('/api/booking-tables', {
        data: {
          booking: bookingDocumentId,
          table: table.documentId,
          name: table.name,
          seatsMin: table.seatsMin,
          seatsMax: table.seatsMax,
          areaSnapshot: {
            bookosId: area.bookosId,
            name: area.name,
            internalNote: area.internalNote ?? '',
            bookingPriority: area.bookingPriority,
          },
        },
      })
      const created = ((resp as any)?.data ?? resp) as Partial<BookingTable>
      const createdTable = created.table && typeof created.table === 'object'
        ? created.table
        : { id: table.id, documentId: table.documentId, name: table.name }
      const createdAreaSnapshot = created.areaSnapshot && typeof created.areaSnapshot === 'object'
        ? created.areaSnapshot
        : {
            bookosId: area.bookosId,
            name: area.name,
            internalNote: area.internalNote ?? '',
            bookingPriority: area.bookingPriority,
          }
      const bookingTable: BookingTable = {
        id: created.id ?? -Date.now(),
        documentId: created.documentId ?? `local-bt-${bookingDocumentId}-${table.documentId}`,
        name: created.name ?? table.name,
        seatsMin: created.seatsMin ?? table.seatsMin,
        seatsMax: created.seatsMax ?? table.seatsMax,
        table: createdTable,
        areaSnapshot: createdAreaSnapshot,
      }
      const booking = await db.bookings.get(bookingDocumentId)
      if (booking) {
        const existing = booking.bookingTables ?? []
        await db.bookings.update(bookingDocumentId, {
          bookingTables: [
            ...existing.filter((bt) => bt.table?.documentId !== table.documentId && bt.documentId !== bookingTable.documentId),
            bookingTable,
          ],
        })
      }
      return resp
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['bookings'] }),
    onError: (error: any) => {
      const msg =
        error?.response?.data?.error?.message ??
        error?.message ??
        'Failed to assign table'
      toast.error(msg)
    },
  })
}

export function useDeleteBookingTable() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (documentId: string) => strapiDelete(`/api/booking-tables/${documentId}`),
    onSuccess: async (_resp, documentId) => {
      const bookings = await db.bookings.toArray()
      const booking = bookings.find((b) => b.bookingTables?.some((bt) => bt.documentId === documentId))
      if (booking) {
        await db.bookings.update(booking.documentId, {
          bookingTables: (booking.bookingTables ?? []).filter((bt) => bt.documentId !== documentId),
        })
      }
      qc.invalidateQueries({ queryKey: ['bookings'] })
    },
  })
}
